<template>
    <div>
        <header-message>Enter Wheel Arch Height</header-message>
        <form class="rideheight">
            <div class="container-fluid">
                <div class="row">
                    <div class="col-6">
                        <form-row :label-width="3">
                            <template #label>
                                <form-label>Front</form-label>
                            </template>
                            <template #fields>
                                <div class="input-group">
                                    <decimal-keyboard v-model="frontLeft"
                                                      css-class="form-control width-4"
                                                      :maxlength="4"
                                                      input-name="rhfl"
                                                      :allow-period="false"
                                                      placeholder="Left"
                                                      @showing="keyboardShown()"
                                                      @hiding="keyboardHidden()"
                                                      :readonly="loading" />
                                    <decimal-keyboard v-model="frontRight"
                                                      css-class="form-control width-4"
                                                      :maxlength="4"
                                                      input-name="rhfr"
                                                      :allow-period="false"
                                                      placeholder="Right"
                                                      @showing="keyboardShown()"
                                                      @hiding="keyboardHidden()"
                                                      :readonly="loading" />
                                    <span class="input-group-text">mm</span>
                                </div>
                            </template>
                        </form-row>
                    </div>
                    <div class="col-6">
                        <form-row :label-width="3">
                            <template #label>
                                <form-label>Rear</form-label>
                            </template>
                            <template #fields>
                                <div class="input-group">
                                    <decimal-keyboard v-model="rearLeft"
                                                      css-class="form-control width-4"
                                                      :maxlength="4"
                                                      input-name="rhrl"
                                                      :allow-period="false"
                                                      placeholder="Left"
                                                      @showing="keyboardShown()"
                                                      @hiding="keyboardHidden()"
                                                      :readonly="loading" />
                                    <decimal-keyboard v-model="rearRight"
                                                      css-class="form-control width-4"
                                                      :maxlength="4"
                                                      input-name="rhrr"
                                                      placeholder="Right"
                                                      :allow-period="false"
                                                      @showing="keyboardShown()"
                                                      @hiding="keyboardHidden()"
                                                      :readonly="loading" />
                                    <span class="input-group-text">mm</span>
                                </div>
                            </template>
                        </form-row>
                    </div>
                </div>
                <div class="row mt-4 riderow">
                    <div class="ridediv">
                        <img src="/img/RideHeight-h400.png" class="img-fluid rideheight" alt="Ride Height">
                    </div>
                </div>
            </div>
        </form>
        <div class="buttonrow">
            <button-button type="button" class="btn btn-secondary me-4" @click.prevent="cancelClicked" :disabled="loading || hasKeyboard">Cancel</button-button>
            <spin-button class="btn-primary" type="submit" @click.prevent="nextClicked" :spinning="loading" :disabled="loading || !isValid() || hasKeyboard">Next</spin-button>
        </div>
    </div>
</template>

<style scoped lang="scss">
    .title {
        font-size: smaller;
        font-weight: bold;
    }
    .riderow {
        position: relative;
    }
    .ridediv {
        position: fixed;
        text-align:center;
        img.rideheight {
            height:45vh;
        }
    }
</style>
<style lang="scss">
    .rideheight {
        .col-lg-9 {
            white-space: nowrap;
            .input-group > div {
                display: inline-block;
            }
            div input.form-control.global-simple-keyboard-input.width-4 { //specificity
                width: 4em;
                font-size: smaller;
            }
        }
        label {
            font-size: smaller;
        }
        .input-group-text {
            font-size: smaller;
            padding-left: 3px;
            padding-right: 3px;
            color: black;
        }
    }
</style>


<script lang="ts">

import { defineComponent } from "vue";

import PageBase from "@/shared/components/common/PageBase";
import HeaderMessage from "@/components/HeaderMessage.vue";
import { ButtonClicked } from "@/shared/enums/ButtonClicked";
import { confirmYesNo, showAlertDialog } from "@/shared/components/common/AlertDialog.vue";
import { Global } from "@/support/GlobalData";
import { SignalR } from "@/support/SignalR";
import { CalibrationEventSupport } from "@/support/CalibrationEvent";
import { CalibrationEvent } from "@/shared/enums/CalibrationEvent";
import { DeviceStatusDto } from "@/shared/models/DeviceStatusDto";

export default defineComponent({
    name: "CalibrationRideHeight",
    mixins: [PageBase],
    components: {
        HeaderMessage,
    },
    data() {
        return {
            frontRight: "",
            frontLeft: "",
            rearRight: "",
            rearLeft: "",
            hasKeyboard: false,
        };
    },
    methods: {
        keyboardShown(): void {
            this.hasKeyboard = true;
        },
        keyboardHidden(): void {
            this.hasKeyboard = false;
        },
        nextClicked(): void {
            const fl = Number(this.frontLeft);
            const fr = Number(this.frontRight);
            const rl = Number(this.rearLeft);
            const rr = Number(this.rearRight);
            if (!this.isValidHeight(fl) || !this.isValidHeight(fr) || !this.isValidHeight(rl) || !this.isValidHeight(rr)) {
                showAlertDialog("Measurement(s) outside normal range for this vehicle. Verify measurements (see picture below) are accurate and entered in millimeters. Contact Auggie support for assistance.",
                    undefined, { okButton: "OK", largeModal: true });
                return;
            }
            CalibrationEventSupport.sendEventRideHeight(CalibrationEvent.RideHeightEntry, fl, fr, rl, rr);
            this.$router.push("/Calibration/CameraSetup");
        },
        isValid(): boolean {
            if (!this.frontLeft.length || isNaN(Number(this.frontLeft))) return false;
            if (!this.frontRight.length || isNaN(Number(this.frontRight))) return false;
            if (!this.rearLeft.length || isNaN(Number(this.rearLeft))) return false;
            if (!this.rearRight.length || isNaN(Number(this.rearRight))) return false;
            return true;
        },
        isValidHeight(height: number): boolean {
            if (!Global.WheelArchHeightMin || !Global.WheelArchHeightMax) return true;
            return Global.WheelArchHeightMin < height && height <= Global.WheelArchHeightMax;
        },

        setRideHeightByCompanion(): void {
            this.$router.push("/Calibration/CameraSetup");
        },

        cancelClicked(): void {
            confirmYesNo("Are you sure you want to cancel this calibration?")
                .then((clicked: ButtonClicked): void => {
                    if (clicked === ButtonClicked.Ok) {
                        CalibrationEventSupport.sendEvent(CalibrationEvent.CalibrationCustomerCanceled);
                        Global.MainApp.clearSession();
                        this.$router.push("/");
                    }
                });
        },

        getCurrentStatusForListeners(): void {
            CalibrationEventSupport.sendEventStatus({
                Page: "CalibrationRideHeight",
            } as DeviceStatusDto);
        }
    },
    mounted(): void {
        if (!Global.CalibrationSession!.WheelArchHeightEnabledInd || (!Global.WheelArchHeightMin && !Global.WheelArchHeightMax)) {
            this.$router.push("/Calibration/CameraSetup");
            return;
        }
        SignalR.onGetCurrentStatusForListeners = this.getCurrentStatusForListeners;
        SignalR.onRideHeightEntryByCompanion = this.setRideHeightByCompanion;
        this.getCurrentStatusForListeners();
    },
    unmounted(): void {
        SignalR.onGetCurrentStatusForListeners = null;
        SignalR.onRideHeightEntryByCompanion = null;
    }
});
</script>
